import {
  Check,
  CheckCircle,
  CheckDone01,
  Download01,
  MessageChatCircle,
} from '@untitledui/icons';
import type { FC } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

import { useOrg } from '../../contexts/OrgContext';
import type { SeatFeature } from './useSeatUpgradeRequest';
import { useSeatUpgradeRequest } from './useSeatUpgradeRequest';

// Relationship note: UpgradePrompt (components/entitlements/UpgradePrompt.tsx) is
// a simpler inline nudge with no async state; SeatUpgradeCard is the viewer-specific
// upgrade path that POST-backs to the backend and manages pending-request state.
// Do not reuse UpgradePrompt here — the async mutation lifecycle differs.

interface FeatureCopy {
  /** The feature's own icon — the card sells the feature, not the lock. */
  icon: FC<{ className?: string }>;
  heading: string;
  /** What the feature does for them, as three short benefits. */
  benefits: readonly [string, string, string];
}

const FEATURE_COPY: Record<SeatFeature, FeatureCopy> = {
  ai_actions: {
    icon: CheckDone01,
    heading: 'AI Actions comes with a full seat',
    benefits: [
      'Review the emails, follow-ups, and CRM updates Ahoy prepares for you',
      'Approve, edit, or dismiss each one before anything happens',
      'Stay on top of every deal without the busywork',
    ],
  },
  chat: {
    icon: MessageChatCircle,
    heading: 'Chat comes with a full seat',
    benefits: [
      'Ask anything about your contacts, companies, and deals',
      'Draft emails and research prospects in seconds',
      'Build reports and dashboards by describing them',
    ],
  },
  crm_export: {
    icon: Download01,
    heading: 'CRM export comes with a full seat',
    benefits: [
      'Export any list or filtered view to CSV',
      'Take your data into spreadsheets and other tools',
      'Exports stay scoped to the records you can see',
    ],
  },
};

// Same explanation on every card: what kind of seat this is and what the
// button does. Feature-specific value lives in the benefits list.
const VIEW_ONLY_BODY =
  "You're on a view-only seat. Request a full seat and we'll let your Account admin know.";
// Once the request is in, the card stops asking the viewer to do something
// they already did and tells them what happens next instead.
const REQUEST_SENT_BODY =
  "We've let your Account admin know. You'll get access as soon as they upgrade your seat.";
const REQUEST_FAILED_MESSAGE = "We couldn't send your request. Try again.";

export interface SeatUpgradeCardProps {
  feature: SeatFeature;
  variant?: 'full' | 'compact';
}

export function SeatUpgradeCard({
  feature,
  variant = 'full',
}: SeatUpgradeCardProps) {
  const { currentOrg } = useOrg();
  const { hasPendingRequest, requestUpgrade, isSubmitting, isError } =
    useSeatUpgradeRequest(currentOrg?.id);

  const { icon: FeatureIcon, heading, benefits } = FEATURE_COPY[feature];
  const isCompact = variant === 'compact';
  const bodyText = hasPendingRequest ? REQUEST_SENT_BODY : VIEW_ONLY_BODY;
  const teamHref = currentOrg
    ? `/${currentOrg.id}/settings/team-members`
    : undefined;

  const requestSent = (
    <div
      role='status'
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full bg-[var(--color-bg-success-secondary)] font-medium text-[var(--color-text-success-primary)]',
        isCompact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
      )}
    >
      <CheckCircle
        className={isCompact ? 'size-3.5' : 'size-4'}
        aria-hidden='true'
      />
      Request sent
    </div>
  );

  if (isCompact) {
    return (
      <div className='flex items-start gap-3 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-3'>
        <FeaturedIcon
          icon={FeatureIcon}
          size='sm'
          theme='light-outline'
          color='brand'
          className='shrink-0'
        />
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <div className='flex flex-col gap-0.5'>
            <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
              {heading}
            </p>
            <p className='text-sm text-[var(--color-text-tertiary)]'>
              {bodyText}
            </p>
            {isError && !hasPendingRequest && (
              <p
                role='alert'
                className='text-sm text-[var(--color-text-error-primary)]'
              >
                {REQUEST_FAILED_MESSAGE}
              </p>
            )}
          </div>
          <div>
            {hasPendingRequest ? (
              requestSent
            ) : (
              <Button
                color='primary'
                size='sm'
                isLoading={isSubmitting}
                onClick={requestUpgrade}
              >
                Request a full seat
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center gap-6 rounded-2xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-8 text-center shadow-xs'>
      <FeaturedIcon icon={FeatureIcon} size='lg' theme='modern' color='brand' />

      <div className='flex flex-col gap-2'>
        <p className='text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'>
          View-only seat
        </p>
        <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>
          {heading}
        </h2>
        <p className='text-sm text-[var(--color-text-secondary)]'>{bodyText}</p>
        {isError && !hasPendingRequest && (
          <p
            role='alert'
            className='text-sm text-[var(--color-text-error-primary)]'
          >
            {REQUEST_FAILED_MESSAGE}
          </p>
        )}
      </div>

      <ul className='flex w-full flex-col gap-2.5 text-left'>
        {benefits.map(benefit => (
          <li
            key={benefit}
            className='flex items-start gap-2.5 text-sm text-[var(--color-text-secondary)]'
          >
            <span className='mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-brand-secondary)]'>
              <Check
                className='size-3 stroke-3 text-[var(--color-fg-brand-primary)]'
                aria-hidden='true'
              />
            </span>
            {benefit}
          </li>
        ))}
      </ul>

      <div className='flex w-full flex-col items-center gap-3'>
        {hasPendingRequest ? (
          requestSent
        ) : (
          <Button
            color='primary'
            size='md'
            isLoading={isSubmitting}
            onClick={requestUpgrade}
            className='w-full'
          >
            Request a full seat
          </Button>
        )}
        {teamHref && (
          <Button color='link-gray' size='sm' href={teamHref}>
            See who manages seats
          </Button>
        )}
      </div>
    </div>
  );
}
